<template>
    <div id="payment-summary" class="content-spacer">
        <div>
            <table id="payment-summary-header" class="drop-shadow-with-top">
                <tr>
                    <td>
                        <div id="paid" :class="{ 'paid-animation': readyToAnimate, 'sprite': true, 'paid': true }" />
                        <div id="corner-img" class="sprite folded-corner" />
                    </td>
                    <td id="title" rowspan="2">
                        {{ title }}
                    </td>
                </tr>
                <tr>
                    <td id="corner-img-spacer" />
                </tr>
            </table>
        </div>
        <div id="rest-of-body" class="drop-shadow-sides">
            <TotalDetail :isTipVisible="true" />
            <div v-show="!(approvedPayments.length === 0)" id="payment-method" class="drop-shadow-sides">
                <table>
                    <tr v-for="payment in approvedPayments" :key="payment.paymentId">
                        <td class="label-column account-type large-bold">
                            <div>{{ payment.paymentName }}</div>
                            <div v-if="hasCardNumber(payment.paymentCard)" class="account-details">
                                Card ending in {{ payment.paymentCard.cardNumber }}
                            </div>
                        </td>
                        <td class="value-column charged-amount large-bold">
                            {{ (payment.amount + payment.tip) | toCurrency }}
                        </td>
                    </tr>
                </table>
            </div>
            <div v-if="!isComplete" id="payment-receipt-option-header" class="drop-shadow-sides">
                How would you like your receipt?
            </div>

            <keep-alive>
                <EmailReceipt v-if="showEmailReceipt"
                              :siteId="session.siteId" :accountId="session.accountId"
                              :checkId="session.checkId" :paymentId="session.payment.paymentId"
                              @cancel="closeEmailReceipt" @sentEmailReceipt="sentEmailReceipt" />
            </keep-alive>

            <div v-if="!isComplete" id="payment-receipt-option" class="large-bold">
                <button v-if="!showEmailReceipt"
                        :class="{'button-secondary-selected': selectedPaymentReceiptOption === 'email'}"
                        @click="selectPaymentReceiptOption('email')" class="button-secondary">
                    Email receipt
                </button>
                <button :class="{'button-secondary-selected': selectedPaymentReceiptOption === 'print'}"
                        @click="selectPaymentReceiptOption('print')" :disabled="showEmailReceipt" class="button-secondary">
                    Print receipt
                </button>
                <button :class="{'button-secondary-selected': selectedPaymentReceiptOption === 'noreceipt'}"
                        @click="selectPaymentReceiptOption('noreceipt')" :disabled="showEmailReceipt" class="button-secondary">
                    No receipt
                </button>
            </div>
            <ThankYouMessage v-if="isComplete" />
        </div>
    </div>
</template>

<script>
    import axios from "axios";
    import TotalDetail from '../components/TotalDetail';
    import EmailReceipt from '../components/EmailReceipt';
    import ThankYouMessage from '../components/ThankYouMessage';
    import { mapState, mapMutations } from "vuex";
    import { required, email } from 'vuelidate/lib/validators';
    import { sweetAlert2 } from "../mixins/sweet-alert2";
    import { showBlocker } from "../mixins/show-blocker";
    import routeHelper from "../router/route-helper";

    const paymentReceiptOptions = {
        none: "none",
        email: "email",
        print: "print",
        noReceipt: "noreceipt"
    }

    export default {
        name: "PaymentSummary",
        mixins: [sweetAlert2, showBlocker],
        components: {
            TotalDetail,
            EmailReceipt,
            ThankYouMessage
        },
        data() {
            return {
                title: "Payment",
                email: undefined, // get this from actual payment view
                showEmailReceipt: false,
                isComplete: false,
                selectedPaymentReceiptOption: paymentReceiptOptions.none,
                readyToAnimate: false
            }
        },
        validations: {
            email: {
                required,
                email
            }
        },
        computed: {
            ...mapState({
                session: state => state.session
            }),
            approvedPayments() {
                return this.session.check.payments
                    .filter(p => p.paymentStatus === 1);
            }
        },
        methods: {
            ...mapMutations([
                "set",
                "setLoadingStatus"
            ]),
            hasCardNumber(paymentCard) {
                return paymentCard != null && paymentCard.CardNumber != null &&
                    paymentCard.CardNumber.trim() !== "";
            },
            async selectPaymentReceiptOption(option) {
                this.selectedPaymentReceiptOption = option;
                if (option === paymentReceiptOptions.print) {
                    this.startProgressIndicator();

                    const requestData = {
                        siteId: this.session.siteId,
                        accountId: this.session.accountId,
                        checkId: this.session.checkId,
                        paymentId: this.session.payment.paymentId,
                    };

                    await axios
                        .post("/payment/printreceipt", requestData)
                        .then(() => {
                                this.isComplete = true;
                                this.swalToast("Receipt printed. Contact staff<br /> to request your receipt.");
                        })
                        .catch(error => {
                            if (error.response &&
                                error.response.data &&
                                error.response.data.errorMessage) {
                                console.error(error.response.data.errorMessage);
                            }
                            else {
                                console.error(error);
                            }

                            this.swalError("1018");
                        })
                        .finally(() => {
                            this.stopProgressIndicator();
                            this.finalize();
                        });
                }
                else if (option === paymentReceiptOptions.email) {
                    // The code in this block is repeated in EmailReceipt.vue.
                    this.showEmailReceipt = false;
                    if (this.$v.email.required && this.$v.email.email) {
                        this.swalConfirm(async () => {
                            this.startProgressIndicator();

                            const requestData = {
                                siteId: this.session.siteId,
                                accountId: this.session.accountId,
                                checkId: this.session.checkId,
                                paymentId: this.session.payment.paymentId,
                                email: this.email
                            };

                            await axios
                                .post("/payment/emailreceipt", requestData)
                                .then(() => {
                                    this.$emit("sentEmailReceipt");
                                })
                                .catch(error => {
                                    if (error.response &&
                                        error.response.data &&
                                        error.response.data.errorMessage) {
                                        console.error(error.response.data.errorMessage);
                                    }
                                    else {
                                        console.error(error);
                                    }
                                    this.swalError("1017");
                                })
                                .finally(() => {
                                    this.stopProgressIndicator();
                                    this.finalize();
                                });

                        }, `Email receipt to ${this.email}?`, "Confirm email address", "OK");
                    }
                    else {
                        this.showEmailReceipt = true;
                    }
                }
                else if (option === paymentReceiptOptions.noReceipt) {
                    this.showEmailReceipt = false;
                    this.isComplete = true;
                    this.finalize();
                }
            },
            async finalize() {
                // The code in this block is repeated in EmailReceipt.vue.
                if (this.session.check.balance > 0) {
                    return;
                }

                const requestUrl = "check/finalize/" + this.session.siteId + "/" + this.session.accountId + "/" + this.session.checkId;

                await axios
                    .post(requestUrl)
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorMessage) {
                            console.error(error.response.data.errorMessage);
                        }
                        else {
                            console.error(error);
                        }
                    });
            },
            closeEmailReceipt() {
                this.showEmailReceipt = false;
                this.selectedPaymentReceiptOption = paymentReceiptOptions.none;
            },
            sentEmailReceipt() {
                this.showEmailReceipt = false;
                this.selectedPaymentReceiptOption = paymentReceiptOptions.none;
                this.isComplete = true;
            }
        },
        async mounted() {
            // query.a stands for 'action' buttons. Value of "0" means hidden.
            // Setting .isComplete = true hides the action buttons.
            if (this.$route.query.a !== undefined && this.$route.query.a === "0") {
                this.isComplete = true;
                this.swalToast("Check paid in full. Thank you.");
            }

            this.setLoadingStatus(false);
            this.readyToAnimate = true;

            routeHelper.preventNavigatingAway();
        }
    }

</script>

<style scoped>
    table {
        width: 100%;
    }

    #payment-summary-header {
        width: 100%;
        text-align: left;
        border-collapse: collapse;
        position: relative;
    }

        #payment-summary-header > tr:not(:first-child) {
            background: var(--white);
        }

        #payment-summary-header td:nth-child(2) {
            background: var(--white);
        }

        #payment-summary-header td:nth-child(1) {
            width: 30px;
        }

        #payment-summary-header > tr > td {
            padding: 0;
        }

    #payment-summary {
        display: flex;
        flex-direction: column;
    }

        #payment-summary > .content-spacer div:nth-child(1) {
            position: relative;
        }

    #corner-img {
        width: 30px;
        height: 30px;
        display: block;
        margin: 0;
    }

    #corner-img-spacer {
        height: 10px;
        border-bottom: var(--border-gray) solid 1px;
    }

    #title {
        font-size: larger;
        font-weight: bolder;
        border-bottom: var(--border-gray) solid 1px;
    }

    #payment-method {
        background-color: var(--white);
        border-bottom: var(--border-gray) solid 1px;
        padding: 10px 7px;
    }

    .account-details {
        font-size: 14px;
    }

    #payment-receipt-option {
        background-color: var(--white);
        padding: 8px 20px 0px 20px;
        text-align: left;
    }

        #payment-receipt-option button {
            margin: 12px auto;
            padding: 12px 0;
            width: 100%;
            display: block;
        }

    #payment-receipt-option-header {
        background-color: var(--white);
        text-align: left;
        padding: 20px 30px 0px 30px;
        font-size: 14px;
    }

    #paid {
        width: 160px;
        position: absolute;
        right: 20px;
        top: 15px;
        opacity: 0;
        z-index: 200;
    }

    .paid-animation {
        animation: paidmark-scale .3s cubic-bezier(.02,1.02,0,1.04) .5s forwards;
    }

    #rest-of-body {
        flex-grow: 1;
        background-color: white;
        padding-bottom: 80px;
    }
</style>